// EnergyOS Dacheditor (pvXpert-Stil) — Vollbild-Trace-Editor über MapLibre.
//
// Architektur: MapLibre bleibt der geo-referenzierte Satelliten-Hintergrund; die
// Zeichenebene ist ein SVG-Overlay (RoofCanvasOverlay), das pro Frame mit
// map.project()/unproject() synchron läuft. Persistenz über die bestehenden
// Building-/RoofSurface-Endpunkte via Props (Vertrag mit ProjectDetailPage).
import {
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import maplibregl from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import { RoofRibbon } from "./roof-editor/RoofRibbon.js";
import { RoofToolPalette } from "./roof-editor/RoofToolPalette.js";
import { RoofCanvasOverlay } from "./roof-editor/RoofCanvasOverlay.js";
import { RoofStepStrip, type RoofStep, type StepState } from "./roof-editor/RoofStepStrip.js";
import { computeBelegung } from "@/lib/roof-belegung.js";
import { RoofDetailsPanel } from "./roof-editor/RoofDetailsPanel.js";
import { evaluateRoofChecklist, buildRoofCopilotAdvice } from "@energyos/roof-core";
import type { RoofSemanticData, RoofEdgeType, ExclusionKind } from "@energyos/shared-types";
import { addEdge, removeEdge, updateEdge, addKeepout, removeKeepout, toggleModuleDisabled, EDGE_TYPE_OPTIONS, KEEPOUT_REASON_OPTIONS } from "./roof-editor/semantic-edit.js";
import type { SemanticSelection } from "./roof-editor/types.js";
import {
  segmentToObject,
  segmentsSignature,
  ROOF_COLORS,
  OBSTACLE_COLOR,
} from "./roof-editor/types.js";
import type {
  RoofSegment,
  EditorObject,
  Tool,
  DetailsTab,
  BgSource,
} from "./roof-editor/types.js";
import {
  areaM2,
  centroidLL,
  centroidOfAll,
  scaleAbout,
  rotateAbout,
  translateByMeters,
  translateByLL,
  bboxOf,
  edgeLengthM,
  toLocalMetersXY,
  type LatLng,
} from "./roof-editor/geo.js";
import { isSimplePolygon } from "@energyos/geometry-core";

export type { RoofSegment } from "./roof-editor/types.js";

interface RoofEditorProps {
  buildingLat?: number;
  buildingLng?: number;
  segments: RoofSegment[];
  onSegmentsChange: (segments: RoofSegment[]) => void;
  onSegmentAdd: (seg: Omit<RoofSegment, "id">) => void;
  onSegmentDelete: (id: string) => void;
  initialBuildingFootprint?: LatLng[];
  onBuildingFootprintChange?: (footprint: LatLng[], areaM2: number) => void;
  onClose?: () => void;
}

// ── MapLibre-Styles ───────────────────────────────────────────────────────────
const SATELLITE_STYLE: maplibregl.StyleSpecification = {
  version: 8,
  sources: {
    esri: {
      type: "raster",
      tiles: [
        "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
      ],
      tileSize: 256,
      attribution: "© Esri, Maxar, USGS",
      maxzoom: 19,
    },
  },
  layers: [{ id: "esri-satellite", type: "raster", source: "esri" }],
};

const CANVAS_STYLE: maplibregl.StyleSpecification = {
  version: 8,
  sources: {},
  layers: [{ id: "background", type: "background", paint: { "background-color": "#eef2f6" } }],
};

function styleFor(src: BgSource): maplibregl.StyleSpecification {
  return src === "canvas" ? CANVAS_STYLE : SATELLITE_STYLE;
}

// 4 Eck-Koordinaten (TL,TR,BR,BL als [lng,lat]) eines georeferenzierten Bildes
// aus Zentrum + Breite [m] + Seitenverhältnis (b/h) + Drehung [°].
function droneCorners(
  c: LatLng,
  widthM: number,
  aspect: number,
  rotDeg: number,
): [[number, number], [number, number], [number, number], [number, number]] {
  const R = 6371000;
  const hw = widthM / 2;
  const hh = widthM / Math.max(aspect, 0.01) / 2;
  const th = (rotDeg * Math.PI) / 180;
  const co = Math.cos(th);
  const si = Math.sin(th);
  const cosLat = Math.cos((c.lat * Math.PI) / 180);
  const offs: Array<[number, number]> = [
    [-hw, hh],
    [hw, hh],
    [hw, -hh],
    [-hw, -hh],
  ];
  const corners = offs.map(([e, n]) => {
    const er = e * co - n * si;
    const nr = e * si + n * co;
    const lng = c.lng + ((er / (cosLat * R)) * 180) / Math.PI;
    const lat = c.lat + ((nr / R) * 180) / Math.PI;
    return [lng, lat] as [number, number];
  });
  return [corners[0]!, corners[1]!, corners[2]!, corners[3]!];
}

function objectToSegment(o: EditorObject): RoofSegment {
  return {
    id: o.id,
    label: o.name,
    tiltDeg: o.tiltDeg,
    azimuthDeg: o.northDeg,
    areaM2: Math.round(areaM2(o.polygon) * 10) / 10,
    polygon: o.polygon,
    color: o.color,
    isObstacle: o.kind === "obstacle",
    heightZ: o.heightZ,
    rotDeg: o.rotDeg,
    scalePct: o.scalePct,
    projektion: o.projektion,
    locked: o.locked,
    hidden: o.hidden,
    semanticJson: o.semantic,
  };
}

export function RoofEditor(props: RoofEditorProps) {
  const {
    buildingLat,
    buildingLng,
    segments,
    onSegmentsChange,
    onSegmentAdd,
    onSegmentDelete,
    initialBuildingFootprint,
    onBuildingFootprintChange,
    onClose,
  } = props;

  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<maplibregl.Map | null>(null);
  const [mapReady, setMapReady] = useState(false);
  const [mapError, setMapError] = useState<string | null>(null);

  // ── Editor-State ──
  const [tool, setTool] = useState<Tool>("select");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  // Stage 2b: aktueller Kantentyp/Reason für neu gezeichnete Objekte + Semantik-Auswahl
  const [currentEdgeType, setCurrentEdgeType] = useState<RoofEdgeType>("first");
  const [currentKeepoutReason, setCurrentKeepoutReason] = useState<ExclusionKind>("edge_setback");
  const [selectedSemantic, setSelectedSemantic] = useState<SemanticSelection | null>(null);
  const [detailsTab, setDetailsTab] = useState<DetailsTab>("gebaeude");
  const [bgEdit, setBgEdit] = useState(false);
  const [bgSource, setBgSource] = useState<BgSource>("sat");
  const [rasterOpacity, setRasterOpacity] = useState(1);
  const [show3d, setShow3d] = useState(false);
  const [showBuilding, setShowBuilding] = useState(true);
  const [showRoofs, setShowRoofs] = useState(true);
  const [showObstacles, setShowObstacles] = useState(true);
  const [hint, setHint] = useState("");
  const [tick, setTick] = useState(0); // Maßstab/Kompass-Refresh

  // ── Drohnenbild-Overlay (georeferenziert, über dem Satellit) ──
  const [drone, setDrone] = useState<{
    url: string;
    center: LatLng;
    widthM: number;
    aspect: number;
    rotDeg: number;
    opacity: number;
  } | null>(null);
  const [droneAlign, setDroneAlign] = useState(false);
  const droneRef = useRef<typeof drone>(null);
  droneRef.current = drone;

  // „All"-Transformationen (Skalieren/Drehen aller Objekte)
  const [allScalePct, setAllScalePct] = useState(100);
  const [allRotDeg, setAllRotDeg] = useState(0);

  // ── Lokale Arbeitskopie (Quelle der Wahrheit während Bearbeitung) ──
  const [objects, setObjects] = useState<EditorObject[]>(() => segments.map(segmentToObject));
  const [buildingPts, setBuildingPts] = useState<LatLng[]>(initialBuildingFootprint ?? []);
  const lastSig = useRef<string>(segmentsSignature(segments));

  // Re-Seed nur bei echter Inhaltsänderung der Props (verhindert Drag-Abbruch).
  const sig = useMemo(() => segmentsSignature(segments), [segments]);
  useEffect(() => {
    if (sig !== lastSig.current) {
      lastSig.current = sig;
      setObjects(segments.map(segmentToObject));
    }
  }, [sig, segments]);

  useEffect(() => {
    setBuildingPts(initialBuildingFootprint ?? []);
  }, [initialBuildingFootprint]);

  // ── Karte initialisieren ──
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    let webglOk = false;
    try {
      const probe = document.createElement("canvas");
      webglOk = !!(probe.getContext("webgl2") || probe.getContext("webgl"));
    } catch {
      webglOk = false;
    }
    if (!webglOk) {
      setMapError("Kartenansicht nicht verfügbar (WebGL deaktiviert).");
      return;
    }
    const center: [number, number] =
      buildingLat !== undefined && buildingLng !== undefined
        ? [buildingLng, buildingLat]
        : [11.3943, 47.2685];
    let map: maplibregl.Map;
    try {
      map = new maplibregl.Map({
        container: containerRef.current,
        style: SATELLITE_STYLE,
        center,
        zoom: 19,
        attributionControl: false,
      });
    } catch {
      setMapError("Kartenansicht nicht verfügbar (WebGL deaktiviert).");
      return;
    }
    mapRef.current = map;
    map.doubleClickZoom.disable(); // Doppelklick schließt Polygon
    map.dragRotate.disable();
    map.touchZoomRotate.disableRotation();
    // Das SVG-Overlay übernimmt Pan + Zoom selbst (einheitlich in allen Werkzeugen),
    // daher MapLibres native Drag/Scroll deaktivieren (kein Doppel-Handling).
    map.dragPan.disable();
    map.scrollZoom.disable();

    map.on("load", () => {
      if (buildingLat !== undefined && buildingLng !== undefined) {
        const el = document.createElement("div");
        el.style.cssText =
          "width:20px;height:20px;background:#ef4444;border:3px solid #fff;border-radius:50% 50% 50% 0;transform:rotate(-45deg);box-shadow:0 2px 6px rgba(0,0,0,0.4);";
        new maplibregl.Marker({ element: el }).setLngLat(center).addTo(map);
      }
      setMapReady(true);
      map.resize();
      // Kein Fit hier: dieser Handler ist im Mount-Effekt registriert und würde
      // fitView() über die (async noch leeren) Mount-Zeit-`objects` aufrufen →
      // easeTo aufs Gebäude, was den korrekten Geometrie-Fit unten überschreibt.
      // Das Einpassen macht ausschließlich der didInitialFit-Effekt (idle-basiert).
    });
    map.on("move", () => setTick((t) => (t + 1) % 1_000_000));

    const onResize = () => map.resize();
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
      map.remove();
      mapRef.current = null;
    };
  }, []);

  // ── Hintergrund-Quelle / Transparenz ──
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady) return;
    map.setStyle(styleFor(bgSource));
    const applyPaint = () => {
      if (bgSource === "canvas") return;
      try {
        map.setPaintProperty("esri-satellite", "raster-opacity", rasterOpacity);
        if (bgSource === "hd") {
          map.setPaintProperty("esri-satellite", "raster-contrast", 0.08);
          map.setPaintProperty("esri-satellite", "raster-saturation", 0.14);
        }
      } catch {
        /* layer noch nicht da */
      }
    };
    void map.once("styledata", applyPaint);
  }, [bgSource]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady || bgSource === "canvas") return;
    try {
      map.setPaintProperty("esri-satellite", "raster-opacity", rasterOpacity);
    } catch {
      /* noop */
    }
  }, [rasterOpacity, mapReady, bgSource]);

  // ── Drohnenbild als georeferenzierte Bildquelle pflegen ──
  const applyDrone = (d: typeof drone) => {
    const map = mapRef.current;
    if (!map || !mapReady) return;
    if (!d) {
      if (map.getLayer("drone-lyr")) map.removeLayer("drone-lyr");
      if (map.getSource("drone-src")) map.removeSource("drone-src");
      return;
    }
    const coords = droneCorners(d.center, d.widthM, d.aspect, d.rotDeg);
    const src = map.getSource<maplibregl.ImageSource>("drone-src");
    if (src) {
      src.updateImage({ url: d.url, coordinates: coords });
    } else {
      map.addSource("drone-src", { type: "image", url: d.url, coordinates: coords });
      map.addLayer({ id: "drone-lyr", type: "raster", source: "drone-src", paint: { "raster-opacity": d.opacity } });
    }
    try {
      map.setPaintProperty("drone-lyr", "raster-opacity", d.opacity);
    } catch {
      /* Layer evtl. noch nicht da */
    }
  };
  useEffect(() => {
    applyDrone(drone);
    // eslint nicht konfiguriert für exhaustive-deps
  }, [drone, mapReady]);
  // Nach Stil-/Quellenwechsel (bgSource) wirft MapLibre Layer weg → neu auflegen.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const h = () => applyDrone(droneRef.current);
    map.on("styledata", h);
    return () => {
      map.off("styledata", h);
    };
  }, [mapReady]);

  const onDroneFile = (file?: File) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const url = reader.result as string;
      const img = new Image();
      img.onload = () => {
        const aspect = img.naturalWidth / Math.max(1, img.naturalHeight) || 1;
        const map = mapRef.current;
        const c = map ? map.getCenter() : { lat: 47.5, lng: 13 };
        setDrone({ url, center: { lat: c.lat, lng: c.lng }, widthM: 40, aspect, rotDeg: 0, opacity: 0.85 });
        setBgEdit(true);
        setDroneAlign(true);
      };
      img.src = url;
    };
    reader.readAsDataURL(file);
  };
  const moveDrone = (dLat: number, dLng: number) =>
    setDrone((d) => (d ? { ...d, center: { lat: d.center.lat + dLat, lng: d.center.lng + dLng } } : d));

  // ── 3D-Pitch ──
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady) return;
    map.easeTo({ pitch: show3d ? 52 : 0, duration: 400 });
  }, [show3d, mapReady]);

  // ── Persistenz-Helfer ──
  const persist = (next: EditorObject[]) => {
    onSegmentsChange(next.map(objectToSegment));
  };

  const updateObjects = (fn: (o: EditorObject) => EditorObject, persistNow = true) => {
    setObjects((prev) => {
      const next = prev.map(fn);
      if (persistNow) persist(next);
      return next;
    });
  };

  // Semantische Daten (Kanten/Sperrflächen) eines Objekts ändern + persistieren.
  const mutateSemantic = (id: string, fn: (d: RoofSemanticData) => RoofSemanticData) => {
    updateObjects((o) => (o.id === id ? { ...o, semantic: fn(o.semantic) } : o));
  };
  const newSemanticId = (): string =>
    typeof crypto !== "undefined" && "randomUUID" in crypto
      ? crypto.randomUUID()
      : `sem-${Date.now()}-${Math.round(performance.now())}`;

  // Zielfläche für ein neu gezeichnetes semantisches Objekt: gewählte Dachfläche,
  // sonst die Dachfläche mit nächstem Zentroid zum Mittelpunkt der Zeichnung.
  const resolveTargetRoofId = (mid: LatLng): string | null => {
    const sel = objects.find((o) => o.id === selectedId);
    if (sel && sel.kind === "roof") return sel.id;
    const roofList = objects.filter((o) => o.kind === "roof" && o.polygon.length >= 3);
    if (roofList.length === 0) return null;
    let best = roofList[0]!;
    let bestD = Infinity;
    for (const r of roofList) {
      const c = centroidLL(r.polygon);
      const d = (c.lat - mid.lat) ** 2 + (c.lng - mid.lng) ** 2;
      if (d < bestD) { bestD = d; best = r; }
    }
    return best.id;
  };

  const handleCreateEdge = (points: LatLng[]) => {
    if (points.length < 2) return;
    const mid = { lat: (points[0]!.lat + points[1]!.lat) / 2, lng: (points[0]!.lng + points[1]!.lng) / 2 };
    const target = resolveTargetRoofId(mid);
    if (!target) { setHint("Bitte zuerst eine Dachfläche anlegen/auswählen."); return; }
    const id = newSemanticId();
    mutateSemantic(target, (d) => addEdge(d, id, currentEdgeType, points.map((p) => ({ lat: p.lat, lng: p.lng }))));
    setSelectedId(target);
    setSelectedSemantic({ surfaceId: target, kind: "edge", id });
    setTool("select");
  };

  const handleCreateKeepout = (points: LatLng[]) => {
    if (points.length < 3) return;
    const mid = centroidLL(points);
    const target = resolveTargetRoofId(mid);
    if (!target) { setHint("Bitte zuerst eine Dachfläche anlegen/auswählen."); return; }
    const id = newSemanticId();
    mutateSemantic(target, (d) => addKeepout(d, id, currentKeepoutReason, points.map((p) => ({ lat: p.lat, lng: p.lng }))));
    setSelectedId(target);
    setSelectedSemantic({ surfaceId: target, kind: "keepout", id });
    setTool("select");
  };

  // Geometrie eines Objekts lokal setzen (während Drag, ohne Persist)
  const handleObjectGeom = (id: string, polygon: LatLng[]) => {
    setObjects((prev) => prev.map((o) => (o.id === id ? { ...o, polygon } : o)));
  };
  const handleCommit = () => {
    setObjects((prev) => {
      persist(prev);
      return prev;
    });
  };

  // ── Derived ──
  const roofs = objects.filter((o) => o.kind === "roof");
  const obstacles = objects.filter((o) => o.kind === "obstacle");
  const totalRoofArea = roofs.reduce((a, o) => a + areaM2(o.polygon), 0);
  const tabObjects = detailsTab === "gebaeude" ? roofs : obstacles;
  const selected = objects.find((o) => o.id === selectedId) ?? null;
  const drawTargetKind: "roof" | "obstacle" = detailsTab === "stoer" ? "obstacle" : "roof";

  // Prüfampel: deterministische Checkliste je Dachmodell (Stage 1). Reine Engine
  // aus roof-core; Eingabe direkt aus den Editor-Objekten gebaut. Kantentypen sind
  // in Stage 1 noch leer → die Ampel meldet korrekt „First/Traufe fehlt".
  const roofChecklist = useMemo(
    () =>
      evaluateRoofChecklist({
        surfaces: roofs.map((o) => ({
          id: o.id,
          label: o.name,
          tiltDeg: o.tiltDeg,
          azimuthDeg: o.northDeg,
          areaM2: areaM2(o.polygon),
          vertexCount: o.polygon.length,
          // Selbstschneidung erkennen (Bowtie) → Prüfampel meldet es ehrlich,
          // statt trotz gekreuzter Kanten „Geometrie ok" grün zu zeigen.
          simple: isSimplePolygon(toLocalMetersXY(o.polygon)),
        })),
        edgeTypes: roofs.flatMap((o) => o.semantic.edges.map((e) => e.type)),
        keepoutCount: roofs.reduce((n, o) => n + o.semantic.keepouts.length, 0),
        obstacles: obstacles.map((o) => ({ id: o.id, type: "other", heightM: o.heightZ ?? null })),
      }).data,
    [roofs, obstacles],
  );

  // KI-Copilot: deterministische Beratung aus der Prüfampel (kein LLM, keine PII).
  const copilotAdvice = useMemo(
    () => (roofChecklist ? buildRoofCopilotAdvice(roofChecklist) : null),
    [roofChecklist],
  );

  // Modulbelegung fürs Overlay (blaue Rechtecke IM Editor). Memoisiert über eine
  // Geometrie-/Traufe-Signatur → NICHT pro Map-Move-Frame neu gerechnet, nur bei echten
  // Edits. Die gezeichnete Traufe richtet die Module parallel zur gewählten Dachseite aus.
  const belegSig =
    JSON.stringify(
      roofs.map((o) => ({
        p: o.polygon,
        t: o.tiltDeg,
        n: o.northDeg,
        e: o.semantic.edges.filter((e) => e.type === "traufe"),
        k: o.semantic.keepouts,
      })),
    ) + "|" + JSON.stringify(obstacles.map((o) => o.polygon));
  // Auto-Belegung (teuer) — nur bei Geometrie-/Keepout-/Traufe-Änderung neu rechnen.
  const rawModuleRects = useMemo(() => {
    const out: { id: string; roofId: string; cellId: string; corners: LatLng[] }[] = [];
    const obsPolys = obstacles.map((o) => o.polygon).filter((p) => p.length >= 3);
    for (const o of roofs) {
      if (o.polygon.length < 3) continue;
      const traufe = o.semantic.edges.find((e) => e.type === "traufe" && e.points.length >= 2);
      const res = computeBelegung({
        polygon: o.polygon,
        ...(obsPolys.length ? { obstacles: obsPolys } : {}),
        keepouts: o.semantic.keepouts.map((k) => k.points).filter((p) => p.length >= 3),
        moduleSpec: { wp: 430, widthM: 1.13, heightM: 1.76 },
        tiltDeg: o.tiltDeg,
        azimuthDeg: o.northDeg,
        ...(traufe ? { eave: { a: traufe.points[0]!, b: traufe.points[traufe.points.length - 1]! } } : {}),
      });
      if (res) for (const m of res.moduleRectsLL) out.push({ id: `${o.id}-${m.id}`, roofId: o.id, cellId: m.id, corners: m.corners });
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [belegSig]);

  // Manuell deaktivierte Module (Zell-IDs je Dachfläche) BILLIG überlagern — ohne
  // die Auto-Belegung neu zu rechnen. Deaktivierte Module bleiben sichtbar (ausgegraut),
  // damit sie wieder aktivierbar sind.
  const disabledSig = roofs.map((o) => `${o.id}:${o.semantic.disabledModules.join(",")}`).join("|");
  const moduleRects = useMemo(() => {
    const disabledByRoof = new Map<string, Set<string>>();
    for (const o of roofs) disabledByRoof.set(o.id, new Set(o.semantic.disabledModules));
    return rawModuleRects.map((m) => ({ ...m, disabled: disabledByRoof.get(m.roofId)?.has(m.cellId) ?? false }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rawModuleRects, disabledSig]);

  // Aktive Modulzahl + kWp (deaktivierte abgezogen) für die Live-Anzeige.
  const moduleStats = useMemo(() => {
    const total = moduleRects.length;
    const disabled = moduleRects.filter((m) => m.disabled).length;
    const active = total - disabled;
    return { total, disabled, active, kwp: Math.round((active * 430) / 10) / 100 };
  }, [moduleRects]);

  const handleToggleModule = (roofId: string, cellId: string) => {
    mutateSemantic(roofId, (d) => toggleModuleDisabled(d, cellId));
  };

  const centerLL: LatLng =
    buildingLat !== undefined && buildingLng !== undefined
      ? { lat: buildingLat, lng: buildingLng }
      : centroidOfAll([...objects.map((o) => o.polygon), buildingPts]) ?? { lat: 47.2685, lng: 11.3943 };

  // ── Maßstab (m/px) am Kartenzentrum ──
  const mpp = useMemo(() => {
    const map = mapRef.current;
    if (!map) return 0.1;
    const c = map.getContainer();
    const cx = c.clientWidth / 2;
    const cy = c.clientHeight / 2;
    const a = map.unproject([cx, cy]);
    const b = map.unproject([cx + 100, cy]);
    return edgeLengthM({ lat: a.lat, lng: a.lng }, { lat: b.lat, lng: b.lng }) / 100;
  }, [tick, mapReady]);
  const scaleBarPx = mpp > 0 ? Math.max(20, 5 / mpp) : 60;

  // ── Aktionen ──────────────────────────────────────────────────────────────
  function fitView() {
    const map = mapRef.current;
    if (!map) return;
    const bb = bboxOf([...objects.map((o) => o.polygon).filter((p) => p.length >= 3), buildingPts].filter((p) => p.length >= 3));
    if (!bb) {
      if (buildingLat !== undefined && buildingLng !== undefined) {
        map.easeTo({ center: [buildingLng, buildingLat], zoom: 19, duration: 400 });
      }
      return;
    }
    map.fitBounds(bb, { padding: 80, maxZoom: 21, duration: 400 });
  }

  // Auto-Fit beim ersten Öffnen: der Fit in map.on("load") feuert oft, BEVOR die
  // Dachflächen (async aus dem segments-Prop) geladen sind → Canvas bliebe schwarz
  // bis zum manuellen "Einpassen". Dieser Effekt passt genau EINMAL ein, sobald
  // Karte UND Geometrie bereit sind (Guard verhindert Re-Fit bei späteren Edits,
  // die das Panning des Nutzers stören würden).
  const didInitialFit = useRef(false);
  useEffect(() => {
    if (didInitialFit.current || !mapReady) return;
    const map = mapRef.current;
    if (!map) return;
    const hasGeometry =
      objects.some((o) => o.polygon.length >= 3) || buildingPts.length >= 3;
    if (!hasGeometry) return;
    didInitialFit.current = true;
    // Robust einpassen: ERST wenn die Karte fertig gelayoutet + Tiles geladen sind
    // ("idle"). Ein fixes Timeout feuert sonst, bevor der Container gemessen ist
    // (frischer Mount → 0-Viewport) → fitBounds passt ins Leere. resize() stellt
    // sicher, dass MapLibre die reale Containergröße kennt. Fallback, falls die
    // Karte bereits idle war (dann kommt kein weiteres "idle").
    let done = false;
    const doFit = () => {
      if (done) return;
      done = true;
      map.resize();
      fitView();
    };
    map.once("idle", doFit);
    const fallback = setTimeout(doFit, 500);
    return () => {
      map.off("idle", doFit);
      clearTimeout(fallback);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapReady, objects, buildingPts]);

  const handleCreatePolygon = (polygon: LatLng[]) => {
    if (polygon.length < 3) return;
    const kind = drawTargetKind;
    const idx = kind === "roof" ? roofs.length : obstacles.length;
    const color = kind === "roof" ? ROOF_COLORS[idx % ROOF_COLORS.length]! : OBSTACLE_COLOR;
    const name =
      kind === "roof" ? `Dachfläche ${idx + 1}` : `Störfläche ${idx + 1}`;
    const a = Math.round(areaM2(polygon) * 10) / 10;
    // optimistisch lokal anzeigen (temporäre ID; Reseed ersetzt sie nach Persist)
    const tempId = `temp-${Date.now()}`;
    setObjects((prev) => [
      ...prev,
      {
        id: tempId,
        kind,
        name,
        color,
        polygon,
        tiltDeg: 30,
        northDeg: 180,
        heightZ: 0,
        rotDeg: 0,
        scalePct: 100,
        projektion: false,
        locked: false,
        hidden: false,
        semantic: { version: 1, edges: [], keepouts: [], disabledModules: [] },
      },
    ]);
    onSegmentAdd({
      label: name,
      tiltDeg: 30,
      azimuthDeg: 180,
      areaM2: a,
      polygon,
      color,
      isObstacle: kind === "obstacle",
      heightZ: 0,
      rotDeg: 0,
      scalePct: 100,
      projektion: false,
      locked: false,
      hidden: false,
    });
    setTool("select");
  };

  const handleDeleteSelected = () => {
    if (!selectedId) return;
    const id = selectedId;
    setSelectedId(null);
    setObjects((prev) => prev.filter((o) => o.id !== id));
    if (!id.startsWith("temp-")) onSegmentDelete(id);
  };

  const handleSnapLocation = () => {
    if (buildingLat === undefined || buildingLng === undefined) {
      fitView();
      return;
    }
    const all = centroidOfAll([...objects.map((o) => o.polygon).filter((p) => p.length >= 3), buildingPts].filter((p) => p.length >= 3));
    if (!all) {
      mapRef.current?.easeTo({ center: [buildingLng, buildingLat], zoom: 19 });
      return;
    }
    const dLat = buildingLat - all.lat;
    const dLng = buildingLng - all.lng;
    const movedObjects = objects.map((o) => ({ ...o, polygon: translateByLL(o.polygon, dLat, dLng) }));
    setObjects(movedObjects);
    persist(movedObjects);
    if (buildingPts.length >= 3) {
      const movedFp = translateByLL(buildingPts, dLat, dLng);
      setBuildingPts(movedFp);
      onBuildingFootprintChange?.(movedFp, Math.round(areaM2(movedFp)));
    }
    setTimeout(() => fitView(), 60);
  };

  const handleSave = () => {
    persist(objects);
    if (buildingPts.length >= 3) {
      onBuildingFootprintChange?.(buildingPts, Math.round(areaM2(buildingPts)));
    }
  };

  const handleReset = () => {
    setSelectedId(null);
    setTool("select");
  };

  // ── Objekteinstellungen (selected oder „alle") ──
  const setName = (name: string) => {
    if (!selected) return;
    updateObjects((o) => (o.id === selected.id ? { ...o, name } : o));
  };
  const setTilt = (deg: number) => {
    if (selected) updateObjects((o) => (o.id === selected.id ? { ...o, tiltDeg: deg } : o));
    else updateObjects((o) => (o.kind === "roof" ? { ...o, tiltDeg: deg } : o));
  };
  // Azimut/Nordwinkel des gewählten Objekts setzen (0=N … 180=S … 270=W),
  // normiert auf 0…360. Wirkt direkt auf den persistierten azimuthDeg.
  const setNorth = (deg: number) => {
    if (!selected) return;
    const n = (((deg % 360) + 360) % 360);
    updateObjects((o) => (o.id === selected.id ? { ...o, northDeg: n } : o));
  };
  const setHeightZ = (m: number) => {
    if (!selected) return;
    updateObjects((o) => (o.id === selected.id ? { ...o, heightZ: m } : o));
  };
  const toggleProjektion = (value: boolean) => {
    if (!selected) return;
    updateObjects((o) => (o.id === selected.id ? { ...o, projektion: value } : o));
  };

  const doScale = (pct: number) => {
    if (pct <= 0) return;
    if (selected) {
      const k = pct / (selected.scalePct || 100);
      const c = centroidLL(selected.polygon);
      updateObjects((o) =>
        o.id === selected.id ? { ...o, scalePct: pct, polygon: scaleAbout(o.polygon, k, c) } : o,
      );
    } else {
      const k = pct / (allScalePct || 100);
      const c = centroidOfAll(objects.map((o) => o.polygon).filter((p) => p.length >= 3));
      if (!c) return;
      setAllScalePct(pct);
      if (buildingPts.length >= 3) {
        const fp = scaleAbout(buildingPts, k, c);
        setBuildingPts(fp);
        onBuildingFootprintChange?.(fp, Math.round(areaM2(fp)));
      }
      updateObjects((o) => ({ ...o, polygon: scaleAbout(o.polygon, k, c) }));
    }
  };

  // Führt den Azimut (northDeg) mit dem Rotations-Delta mit.
  // rotateAbout(deg>0) dreht das Polygon im East/North-Frame gegen den
  // Uhrzeigersinn (Ost→Nord). Der Azimut ist hingegen IM Uhrzeigersinn von Nord
  // gemessen (0=N, 90=O, 180=S, 270=W) — eine CCW-Drehung der Fläche senkt den
  // Azimut also um delta. Vorzeichen daher (northDeg - delta), normiert auf 0…360.
  const rotateNorth = (northDeg: number, delta: number): number =>
    (((northDeg - delta) % 360) + 360) % 360;

  const doRotate = (deg: number) => {
    if (selected) {
      const delta = deg - (selected.rotDeg || 0);
      const c = centroidLL(selected.polygon);
      updateObjects((o) =>
        o.id === selected.id
          ? { ...o, rotDeg: deg, northDeg: rotateNorth(o.northDeg, delta), polygon: rotateAbout(o.polygon, delta, c) }
          : o,
      );
    } else {
      const delta = deg - allRotDeg;
      const c = centroidOfAll(objects.map((o) => o.polygon).filter((p) => p.length >= 3));
      if (!c) return;
      setAllRotDeg(deg);
      if (buildingPts.length >= 3) {
        const fp = rotateAbout(buildingPts, delta, c);
        setBuildingPts(fp);
        onBuildingFootprintChange?.(fp, Math.round(areaM2(fp)));
      }
      updateObjects((o) => ({ ...o, northDeg: rotateNorth(o.northDeg, delta), polygon: rotateAbout(o.polygon, delta, c) }));
    }
  };

  const setPosition = (axis: "x" | "y", meters: number) => {
    if (!selected) return;
    // aktuelle Meter-Position des Zentroids relativ zum Projektzentrum
    const c = centroidLL(selected.polygon);
    const curX = edgeLengthM({ lat: c.lat, lng: centerLL.lng }, c) * (c.lng >= centerLL.lng ? 1 : -1);
    const curY = edgeLengthM({ lat: centerLL.lat, lng: c.lng }, c) * (c.lat >= centerLL.lat ? 1 : -1);
    const dx = axis === "x" ? meters - curX : 0;
    const dy = axis === "y" ? meters - curY : 0;
    updateObjects((o) =>
      o.id === selected.id ? { ...o, polygon: translateByMeters(o.polygon, dx, dy) } : o,
    );
  };

  const toggleHidden = (id: string) => {
    updateObjects((o) => (o.id === id ? { ...o, hidden: !o.hidden } : o));
  };
  const toggleLocked = (id: string) => {
    updateObjects((o) => (o.id === id ? { ...o, locked: !o.locked } : o));
  };

  // ── Anfänger-Schritt-Streifen: Zustände aus vorhandenem State ableiten ──────
  // Rein führend (nie blockierend). Klick springt zum passenden Werkzeug.
  const steps: RoofStep[] = useMemo(() => {
    const buildingDone = buildingPts.length >= 3;
    const roofDone = roofs.some((o) => o.polygon.length >= 3);
    const traufeDone = roofs.some((o) =>
      o.semantic.edges.some((e) => e.type === "traufe" && e.points.length >= 2),
    );
    const modulesDone = moduleRects.length > 0;

    const base = [
      {
        key: "building",
        label: "Gebäude",
        hint: "Gebäude-Grundriss festlegen (aus Standort übernommen).",
        done: buildingDone,
        onClick: () => setShowBuilding(true),
      },
      {
        key: "roof",
        label: "Dachfläche",
        hint: "Dachfläche als Rechteck oder Polygon zeichnen.",
        done: roofDone,
        onClick: () => {
          setDetailsTab("gebaeude");
          setTool("rect");
        },
      },
      {
        key: "edges",
        label: "First & Traufe",
        hint: "Traufkante einzeichnen → Module liegen dach-parallel (Süd-/Wunschseite).",
        done: traufeDone,
        onClick: () => setTool("edge"),
      },
      {
        key: "modules",
        label: "Module",
        hint: "Module werden automatisch belegt — Ergebnis auf der Karte prüfen.",
        done: modulesDone,
        onClick: undefined as (() => void) | undefined,
      },
    ];

    // Erster nicht-erledigter Schritt = „current"; danach „todo".
    let firstOpen = false;
    const out: RoofStep[] = base.map(({ done, ...rest }) => {
      let state: StepState;
      if (done) state = "done";
      else if (!firstOpen) {
        firstOpen = true;
        state = "current";
      } else state = "todo";
      return { ...rest, state };
    });

    // Schritt 5 „Prüfen" aus der Prüfampel: ok=erledigt, warning/advice=Achtung.
    const verdict = roofChecklist?.verdict;
    const reviewState: StepState =
      verdict === "ok"
        ? "done"
        : verdict === "warning" || verdict === "advice"
        ? "warn"
        : !firstOpen
        ? "current"
        : "todo";
    out.push({
      key: "review",
      label: "Prüfen",
      hint: "Prüfampel rechts: offene Warnungen abarbeiten (ENTWURF, keine Freigabe).",
      state: reviewState,
      onClick: () => setDetailsTab("gebaeude"),
    });
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buildingPts, roofs, moduleRects, roofChecklist]);

  // ── Render ─────────────────────────────────────────────────────────────────
  const projectLabel = "Gebäude 1";

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 1000,
        display: "flex",
        flexDirection: "column",
        height: "100vh",
        background: "#0e1c2e",
        overflow: "hidden",
        userSelect: "none",
      }}
    >
      <RoofRibbon
        roofCount={roofs.length}
        totalAreaM2={totalRoofArea}
        projectLabel={projectLabel}
        showBackground={bgEdit}
        showBuilding={showBuilding}
        show3d={show3d}
        saving={false}
        onSave={handleSave}
        onImport={fitView}
        onReset={handleReset}
        onDelete={handleDeleteSelected}
        onToggleBackground={() => setBgEdit((v) => !v)}
        onToggleBuilding={() => setShowBuilding((v) => !v)}
        onToggle3d={() => setShow3d((v) => !v)}
        onSnapLocation={handleSnapLocation}
        onClose={() => onClose?.()}
      />

      {!show3d && <RoofStepStrip steps={steps} />}

      <div style={{ flex: 1, display: "flex", minHeight: 0 }}>
        <RoofToolPalette tool={tool} onToolChange={setTool} disabled={show3d} />

        {/* Viewport */}
        <div style={{ flex: 1, position: "relative", overflow: "hidden", background: "#1a2433" }}>
          {mapError ? (
            <div
              style={{
                position: "absolute",
                inset: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#cbd5e1",
                fontSize: 14,
                textAlign: "center",
                padding: 24,
              }}
            >
              {mapError}
            </div>
          ) : (
            <>
              <div ref={containerRef} style={{ position: "absolute", inset: 0 }} />
              {mapReady && (
                <RoofCanvasOverlay
                  map={mapRef.current}
                  tool={tool}
                  paused={show3d}
                  objects={objects}
                  buildingPts={buildingPts}
                  showBuilding={showBuilding}
                  showRoofs={showRoofs}
                  showObstacles={showObstacles}
                  moduleRects={moduleRects}
                  onToggleModule={handleToggleModule}
                  selectedId={selectedId}
                  drawTargetKind={drawTargetKind}
                  onSelect={(id) => { setSelectedId(id); setSelectedSemantic(null); }}
                  onObjectGeom={handleObjectGeom}
                  onCommit={handleCommit}
                  onCreatePolygon={handleCreatePolygon}
                  onCreateEdge={handleCreateEdge}
                  onCreateKeepout={handleCreateKeepout}
                  selectedSemantic={selectedSemantic}
                  onSelectSemantic={(sel) => { setSelectedSemantic(sel); if (sel) setSelectedId(sel.surfaceId); }}
                  onHint={setHint}
                  onDeleteSelected={handleDeleteSelected}
                  droneAlign={!!drone && droneAlign}
                  onDroneMove={moveDrone}
                />
              )}

              {/* Oben-Mitte: Typ-/Reason-Wahl für Zeichenmodi (Stage 2b) */}
              {(tool === "edge" || tool === "keepout") && (
                <div style={{ position: "absolute", top: 10, left: "50%", transform: "translateX(-50%)", zIndex: 13, background: "#fff", border: "1px solid #cbd5e1", borderRadius: 8, padding: "5px 10px", display: "flex", alignItems: "center", gap: 8, boxShadow: "0 1px 4px rgba(0,0,0,.12)" }}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: "#1e293b" }}>
                    {tool === "edge" ? "Kantentyp:" : "Sperrfläche:"}
                  </span>
                  {tool === "edge" ? (
                    <select value={currentEdgeType} onChange={(e) => setCurrentEdgeType(e.target.value as RoofEdgeType)} style={{ fontSize: 12, padding: "2px 6px" }}>
                      {EDGE_TYPE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                  ) : (
                    <select value={currentKeepoutReason} onChange={(e) => setCurrentKeepoutReason(e.target.value as ExclusionKind)} style={{ fontSize: 12, padding: "2px 6px" }}>
                      {KEEPOUT_REASON_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                  )}
                </div>
              )}

              {/* Modul-Modus: Live-Zahl + Anleitung */}
              {tool === "module" && (
                <div style={{ position: "absolute", top: 10, left: "50%", transform: "translateX(-50%)", zIndex: 13, background: "#fff", border: "1px solid #cbd5e1", borderRadius: 8, padding: "5px 12px", display: "flex", alignItems: "center", gap: 10, boxShadow: "0 1px 4px rgba(0,0,0,.12)" }}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: "#2563eb" }}>{moduleStats.active} Module · {moduleStats.kwp.toFixed(2)} kWp</span>
                  {moduleStats.disabled > 0 && <span style={{ fontSize: 11, color: "#64748b" }}>{moduleStats.disabled} deaktiviert</span>}
                  <span style={{ fontSize: 11, color: "#475569" }}>· Modul anklicken = ein/aus</span>
                </div>
              )}

              {/* Oben-links: Quellen-Tabs + Ebenen-Pills */}
              <div style={{ position: "absolute", top: 10, left: 10, zIndex: 12, display: "flex", flexDirection: "column", gap: 6 }}>
                <div
                  style={{
                    display: "flex",
                    background: "rgba(255,255,255,0.96)",
                    borderRadius: 7,
                    padding: 3,
                    boxShadow: "0 2px 8px rgba(0,0,0,0.25)",
                  }}
                >
                  {([
                    ["canvas", "Canvas"],
                    ["sat", "Satellit"],
                    ["hd", "Satellit HD"],
                  ] as Array<[BgSource, string]>).map(([s, label]) => {
                    const active = bgSource === s;
                    return (
                      <button
                        key={s}
                        type="button"
                        onClick={() => setBgSource(s)}
                        style={{
                          padding: "4px 10px",
                          fontSize: 11,
                          border: "none",
                          borderRadius: 5,
                          cursor: "pointer",
                          background: active ? "#2563eb" : "transparent",
                          color: active ? "#fff" : "#334155",
                          fontWeight: active ? 600 : 500,
                        }}
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>
                <div style={{ display: "flex", gap: 5 }}>
                  {([
                    ["Gebäude", showBuilding, () => setShowBuilding((v) => !v), "#ef4444"],
                    ["Dach", showRoofs, () => setShowRoofs((v) => !v), "#2563eb"],
                    ["Störfläche", showObstacles, () => setShowObstacles((v) => !v), "#d97706"],
                  ] as Array<[string, boolean, () => void, string]>).map(([label, on, toggle, dot]) => (
                    <button
                      key={label}
                      type="button"
                      onClick={toggle}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 5,
                        padding: "4px 9px",
                        fontSize: 11,
                        borderRadius: 6,
                        border: `1px solid ${on ? "#cbd5e1" : "#e2e8f0"}`,
                        background: on ? "rgba(255,255,255,0.96)" : "rgba(255,255,255,0.6)",
                        color: on ? "#1e293b" : "#94a3b8",
                        cursor: "pointer",
                      }}
                    >
                      <span style={{ width: 8, height: 8, borderRadius: 2, background: on ? dot : "#cbd5e1" }} />
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Oben-rechts: Zoom-Stack */}
              <div
                style={{
                  position: "absolute",
                  top: 10,
                  right: 10,
                  zIndex: 12,
                  display: "flex",
                  flexDirection: "column",
                  background: "rgba(255,255,255,0.96)",
                  borderRadius: 7,
                  overflow: "hidden",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.25)",
                }}
              >
                <button type="button" onClick={() => mapRef.current?.zoomIn()} style={zoomBtn}>
                  +
                </button>
                <button type="button" onClick={() => mapRef.current?.zoomOut()} style={{ ...zoomBtn, borderTop: "1px solid #e2e8f0" }}>
                  −
                </button>
                <button type="button" onClick={fitView} title="Einpassen" style={{ ...zoomBtn, borderTop: "1px solid #e2e8f0" }}>
                  ⊡
                </button>
              </div>

              {/* Unten-rechts: Kompass */}
              <div
                style={{
                  position: "absolute",
                  bottom: 46,
                  right: 14,
                  zIndex: 12,
                  width: 50,
                  height: 50,
                  borderRadius: "50%",
                  background: "rgba(255,255,255,0.92)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
                  pointerEvents: "none",
                }}
              >
                <svg width={40} height={40} viewBox="0 0 40 40">
                  <circle cx={20} cy={20} r={18} fill="none" stroke="#cbd5e1" strokeWidth={1} />
                  <path d="M20 5l4.5 15h-9z" fill="#ef4444" />
                  <path d="M20 35l-4.5-15h9z" fill="#94a3b8" />
                  <text x={20} y={14} fontSize={7.5} fill="#0f172a" textAnchor="middle" fontWeight={800}>
                    N
                  </text>
                </svg>
              </div>

              {/* Unten-links: Maßstabsbalken */}
              <div style={{ position: "absolute", bottom: 14, left: 14, zIndex: 12, pointerEvents: "none", display: "flex", flexDirection: "column", gap: 2 }}>
                <div style={{ width: scaleBarPx, height: 6, border: "2px solid #fff", borderTop: "none", boxShadow: "0 1px 2px rgba(0,0,0,0.6)" }} />
                <span style={{ fontSize: 10, color: "#fff", textShadow: "0 1px 2px rgba(0,0,0,0.85)", fontWeight: 600 }}>5 m</span>
              </div>

              {/* Unten-mitte: Hinweis-Pill */}
              {hint && (
                <div
                  style={{
                    position: "absolute",
                    bottom: 14,
                    left: "50%",
                    transform: "translateX(-50%)",
                    zIndex: 12,
                    background: "rgba(14,28,46,0.82)",
                    color: "#fff",
                    fontSize: 11,
                    fontWeight: 500,
                    padding: "5px 14px",
                    borderRadius: 20,
                    pointerEvents: "none",
                    maxWidth: "78%",
                    textAlign: "center",
                  }}
                >
                  {hint}
                </div>
              )}
            </>
          )}
        </div>

        <RoofDetailsPanel
          detailsTab={detailsTab}
          onTabChange={setDetailsTab}
          bgEdit={bgEdit}
          rasterOpacity={rasterOpacity}
          onRasterOpacity={setRasterOpacity}
          mpp={mpp}
          bgExtra={
            <div style={{ marginTop: 12, borderTop: "1px solid #d4dde6", paddingTop: 10 }}>
              <p style={{ fontSize: 11, fontWeight: 700, margin: "0 0 6px", color: "#1e40af" }}>Drohnenbild</p>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => onDroneFile(e.target.files?.[0])}
                style={{ fontSize: 11, width: "100%" }}
              />
              {drone && (
                <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 8 }}>
                  <label style={{ fontSize: 11, color: "#374151" }}>
                    Transparenz: {Math.round(drone.opacity * 100)} %
                    <input type="range" min={10} max={100} value={Math.round(drone.opacity * 100)}
                      onChange={(e) => setDrone((d) => (d ? { ...d, opacity: Number(e.target.value) / 100 } : d))}
                      style={{ width: "100%", accentColor: "#2563eb" }} />
                  </label>
                  <label style={{ fontSize: 11, color: "#374151" }}>
                    Breite: {drone.widthM} m
                    <input type="range" min={3} max={200} value={drone.widthM}
                      onChange={(e) => setDrone((d) => (d ? { ...d, widthM: Number(e.target.value) } : d))}
                      style={{ width: "100%", accentColor: "#2563eb" }} />
                  </label>
                  <label style={{ fontSize: 11, color: "#374151" }}>
                    Drehung: {drone.rotDeg}°
                    <input type="range" min={-180} max={180} value={drone.rotDeg}
                      onChange={(e) => setDrone((d) => (d ? { ...d, rotDeg: Number(e.target.value) } : d))}
                      style={{ width: "100%", accentColor: "#2563eb" }} />
                  </label>
                  <label style={{ fontSize: 11, color: "#374151", display: "flex", alignItems: "center", gap: 6 }}>
                    <input type="checkbox" checked={droneAlign} onChange={(e) => setDroneAlign(e.target.checked)} />
                    Ausrichten (Bild ziehen statt Karte)
                  </label>
                  <button type="button" onClick={() => { setDrone(null); setDroneAlign(false); }}
                    style={{ fontSize: 11, padding: "4px 8px", border: "1px solid #ef4444", borderRadius: 5, background: "#fff", color: "#ef4444", cursor: "pointer" }}>
                    Drohnenbild entfernen
                  </button>
                </div>
              )}
            </div>
          }
          objects={tabObjects}
          selected={selected}
          checklist={roofChecklist}
          copilotAdvice={copilotAdvice}
          onAddEdge={(type: RoofEdgeType) => {
            if (!selected) return;
            const poly = selected.polygon;
            const seed = poly.length >= 2 ? [poly[0]!, poly[1]!] : [];
            mutateSemantic(selected.id, (d) => addEdge(d, newSemanticId(), type, seed));
          }}
          onUpdateEdge={(edgeId, patch) => selected && mutateSemantic(selected.id, (d) => updateEdge(d, edgeId, patch))}
          onRemoveEdge={(edgeId) => selected && mutateSemantic(selected.id, (d) => removeEdge(d, edgeId))}
          onAddKeepout={(kind: ExclusionKind) => {
            if (!selected) return;
            mutateSemantic(selected.id, (d) => addKeepout(d, newSemanticId(), kind, selected.polygon));
          }}
          onRemoveKeepout={(koId) => selected && mutateSemantic(selected.id, (d) => removeKeepout(d, koId))}
          roofCount={roofs.length}
          obstacleCount={obstacles.length}
          totalRoofAreaM2={totalRoofArea}
          allScalePct={allScalePct}
          allRotDeg={allRotDeg}
          onSelect={setSelectedId}
          onSetName={setName}
          onSetTilt={setTilt}
          onSetNorth={setNorth}
          onSetHeightZ={setHeightZ}
          onSetPosition={setPosition}
          onScale={doScale}
          onRotate={doRotate}
          onToggleProjektion={toggleProjektion}
          onToggleHidden={toggleHidden}
          onToggleLocked={toggleLocked}
          onDelete={handleDeleteSelected}
        />
      </div>
    </div>
  );
}

const zoomBtn: React.CSSProperties = {
  width: 32,
  height: 30,
  border: "none",
  background: "#fff",
  fontSize: 17,
  cursor: "pointer",
  color: "#1e293b",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};
